/**
 * The records the examiner read, each once however many lookups returned
 * it: what every desk variant ranks, groups and marks. A record seen in a
 * list and later opened is one record, opened, that knows the lists.
 *
 * @module components/apps/probe/desk/deskRecords
 */
import type { ProbeRecord } from "@amoreg/contracts";

import { recordKey } from "../../../../state/probeRecords";
import { SEVERITY_ORDER } from "../../../regulation/finding/findingItem";
import { deskDate, type ProbeDeskLink, type ProbeDeskLookup, type ProbeDeskLookups } from "../ProbeDesk.logic";

export type DeskKind = ProbeRecord["kind"];

/** The order a reader wants kinds in: what the examiner will press on first. */
export const DESK_KINDS = [
  "finding",
  "policy",
  "update",
  "passage",
  "web",
  "rehearsal",
] as const satisfies ReadonlyArray<DeskKind>;

export interface DeskRecord {
  readonly key: string;
  readonly kind: DeskKind;
  readonly title: string;
  readonly severity: string | null;
  /**
   * One quiet line: a finding's summary, a policy's state and review date, a
   * regulator change's place and date, a passage's words, a page's site.
   */
  readonly detail: string | null;
  /** What a row shows opened: a finding's summary or a passage's words, then its facts. */
  readonly more: DeskRecordMore;
  readonly link: ProbeDeskLink | null;
  /** Opened, or returned by a search, rather than only seen in a list. */
  readonly opened: boolean;
  /** The lists it was seen in, by subject: "the open findings". */
  readonly listedIn: ReadonlyArray<string>;
  /** A web page that is a regulator change in the file: that change's title. */
  readonly filedAs: string | null;
}

/** One fact of an opened row's grid: "Owner", "Alex Morgan". */
export interface DeskField {
  readonly label: string;
  readonly value: string;
}

export interface DeskRecordMore {
  readonly text: string | null;
  /** Where the record stands, as a badge: a finding's status, a policy's lifecycle. */
  readonly status: string | null;
  /** Only the facts the record carries, each under its label. */
  readonly fields: ReadonlyArray<DeskField>;
}

const sentence = (phrase: string) => phrase.charAt(0).toUpperCase() + phrase.slice(1);

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const joined = (parts: ReadonlyArray<string | null>): string | null => {
  const kept = parts.filter((part): part is string => part !== null && part.length > 0);
  return kept.length === 0 ? null : kept.join(" · ");
};

const detailOf = (record: ProbeRecord, lookups: ProbeDeskLookups): string | null => {
  switch (record.kind) {
    case "finding":
      return lookups.cardOf(record).detail;
    case "policy": {
      const facts = lookups.policyFacts(record.id);
      return facts === null
        ? null
        : joined([
            facts.lifecycle === null ? null : sentence(facts.lifecycle.replace(/_/g, " ")),
            facts.reviewDue === null ? null : `review due ${deskDate(facts.reviewDue)}`,
          ]);
    }
    case "update":
      return joined([record.jurisdiction, record.date === null ? null : deskDate(record.date)]);
    case "passage":
      return record.text;
    case "web":
      return hostOf(record.url);
    case "rehearsal":
      return deskDate(record.date);
  }
};

const fields = (pairs: ReadonlyArray<readonly [string, string | null]>): ReadonlyArray<DeskField> =>
  pairs.flatMap(([label, value]) =>
    value === null || value.trim().length === 0 ? [] : [{ label, value }],
  );

const stated = (phrase: string | null): string | null =>
  phrase === null || phrase.trim().length === 0 ? null : sentence(phrase.replace(/_/g, " "));

const moreOf = (record: ProbeRecord, lookups: ProbeDeskLookups): DeskRecordMore => {
  switch (record.kind) {
    case "finding":
      return {
        text: lookups.cardOf(record).detail,
        status: stated(record.status),
        fields: fields([["Severity", stated(record.severity)]]),
      };
    case "policy": {
      const facts = lookups.policyFacts(record.id);
      return {
        text: null,
        status: stated(facts?.lifecycle ?? null),
        fields: fields([
          ["Owner", facts?.owner == null ? null : lookups.ownerName(facts.owner)],
          ["Review due", facts?.reviewDue == null ? null : deskDate(facts.reviewDue)],
        ]),
      };
    }
    case "update":
      return {
        text: null,
        status: null,
        fields: fields([
          ["Published", record.date === null ? null : deskDate(record.date)],
          ["Jurisdiction", record.jurisdiction],
          ["Source", record.url === null ? null : hostOf(record.url)],
        ]),
      };
    case "passage":
      return { text: record.text, status: null, fields: fields([["Source", record.source]]) };
    case "web":
      return { text: null, status: null, fields: fields([["Site", hostOf(record.url)]]) };
    case "rehearsal":
      return { text: null, status: null, fields: fields([["Held", deskDate(record.date)]]) };
  }
};

const severityRank = (severity: string | null): number => {
  const at = SEVERITY_ORDER.findIndex((one) => one === severity);
  return at === -1 ? SEVERITY_ORDER.length : at;
};

/** Kinds in `DESK_KINDS` order, findings most severe first; otherwise the order given. */
export const byImportance = (one: DeskRecord, other: DeskRecord): number =>
  DESK_KINDS.indexOf(one.kind) - DESK_KINDS.indexOf(other.kind) ||
  severityRank(one.severity) - severityRank(other.severity);

/** A later lookup that knew less (a policy read only as text) keeps what an earlier one knew. */
const richer = (next: DeskRecordMore, known: DeskRecordMore | undefined): DeskRecordMore =>
  known === undefined
    ? next
    : {
        text: next.text ?? known.text,
        status: next.status ?? known.status,
        fields: next.fields.length > 0 ? next.fields : known.fields,
      };

/** Every record the lookups returned, once, in the order first read. */
export const deskRecordsOf = (
  finished: ReadonlyArray<ProbeDeskLookup>,
  lookups: ProbeDeskLookups,
): ReadonlyArray<DeskRecord> => {
  const records = new Map<string, DeskRecord>();
  for (const lookup of finished) {
    if (lookup.kind !== "list" && lookup.kind !== "opened") continue;
    for (const record of lookup.records) {
      const key = recordKey(record);
      const known = records.get(key);
      const card = lookups.cardOf(record);
      const opened = (known?.opened ?? false) || lookup.kind === "opened";
      const listedIn =
        lookup.kind === "list" && !(known?.listedIn.includes(lookup.subject) ?? false)
          ? [...(known?.listedIn ?? []), lookup.subject]
          : (known?.listedIn ?? []);
      const filedAs =
        record.kind === "web"
          ? (lookups.items.updates.find((update) => update.url === record.url)?.title ?? null)
          : null;
      records.set(key, {
        key,
        kind: record.kind,
        // A policy read only as text is named by a later lookup that knew it.
        title: card.title,
        severity: card.severity ?? known?.severity ?? null,
        detail: detailOf(record, lookups) ?? known?.detail ?? null,
        more: richer(moreOf(record, lookups), known?.more),
        link: card.link ?? known?.link ?? null,
        opened,
        listedIn,
        filedAs,
      });
    }
  }
  return [...records.values()];
};

/** A lookup that returned no record. */
export type DeskCheck =
  | {
      readonly kind: "failed";
      readonly key: string;
      readonly title: string;
      readonly reason: string | null;
    }
  | {
      readonly kind: "nothing";
      readonly key: string;
      readonly title: string;
      readonly query: string | null;
      /** The read succeeded and carries no record (the profile, the people), rather than finding nothing. */
      readonly checked: boolean;
    };

/**
 * The lookups that returned no record: failures first, in the order they
 * failed, then what came back empty, once per lookup. A list that came back
 * empty and later held rows is not empty.
 */
export const deskChecksOf = (finished: ReadonlyArray<ProbeDeskLookup>): ReadonlyArray<DeskCheck> => {
  const failed: Array<DeskCheck> = [];
  const empty = new Map<string, DeskCheck>();
  for (const lookup of finished) {
    switch (lookup.kind) {
      case "failed":
        failed.push({ kind: "failed", key: lookup.callId, title: lookup.title, reason: lookup.reason });
        break;
      case "nothing": {
        const { callId: _callId, ...nothing } = lookup;
        empty.set(lookup.key, nothing);
        break;
      }
      case "list":
        if (lookup.holds !== null && lookup.records.length > 0) empty.delete(lookup.key);
        else
          empty.set(lookup.key, {
            kind: "nothing",
            key: lookup.key,
            title:
              lookup.holds === null
                ? `Looked through ${lookup.subject}`
                : `Looked through ${lookup.subject}: none there`,
            query: null,
            checked: lookup.holds === null,
          });
        break;
      case "opened":
        break;
    }
  }
  return [...failed, ...empty.values()];
};
